/**
 * Recognises the failures of `POST /paywall/feature-subscriptions/` that only Suno support
 * can resolve, so the caller can present them as information rather than as a transient
 * error snackbar.
 *
 * The endpoint's failure responses all carry an `error` key, so "has an `error` key"
 * over-matches. The rule is therefore **status is exactly 400 AND `data.error` is a string**
 * (see suno-backend `paywall/views/feature_subscription.py`):
 *
 *   400 {error, subscription_id}  "already has an active subscription"  -> support
 *   400 {error}                   "not available for purchase"          -> support
 *   400 {error}                   "does not have Stripe customer setup" -> support
 *   409 {error}                   "billing details just changed"        -> NOT support (`getPaywallConflictMessage`)
 *   500 {error}                   "failed to create checkout session"   -> NOT support (retryable)
 *   400 {field: [msg]}            DRF field validation                  -> NOT support (no `error` key)
 *
 * Deliberately matches no English text, so rewording any of those messages cannot silently
 * turn the modal off.
 */

export interface PaywallSupportError {
  /** The backend's own sentence, shown to the user verbatim. */
  message: string;
  /** Only the "already has an active subscription" branch sends this. */
  subscriptionId?: number;
}

export function getPaywallSupportError(
  error: unknown
): PaywallSupportError | null {
  if (!error || typeof error !== "object") return null;

  // `response.error` is `FetchBaseQueryError | SerializedError`, and SerializedError has no
  // `status` at all — so this must be checked before reading it.
  if (!("status" in error)) return null;
  // A strict !== 400 also rules out the string-status arms ("FETCH_ERROR", "TIMEOUT_ERROR",
  // "PARSING_ERROR", "CUSTOM_ERROR"), where `data` is absent or a raw string.
  if ((error as { status: unknown }).status !== 400) return null;

  if (!("data" in error)) return null;
  const data = (error as { data: unknown }).data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;

  const message = (data as { error?: unknown }).error;
  if (typeof message !== "string" || !message.trim()) return null;

  // The SUCCESS response types `subscription_id` as a string; this 400 sends a number. Do
  // not reuse `PaywallFeatureSubscriptionCreateResponse` for the error body.
  const rawId = (data as { subscription_id?: unknown }).subscription_id;
  const hasId = typeof rawId === "number" && Number.isFinite(rawId);

  return {
    message: message.trim(),
    ...(hasId ? { subscriptionId: rawId as number } : {})
  };
}

/**
 * Scribe checkout's 409 (SN-19868): the practice's Stripe customer or price changed while the
 * request was in flight. Nothing was charged and trying again works, so the caller shows the
 * backend's sentence where the purchase was made rather than a support modal or a snackbar.
 */
export function getPaywallConflictMessage(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("status" in error)) return null;
  if ((error as { status: unknown }).status !== 409 || !("data" in error)) {
    return null;
  }
  const data = (error as { data: unknown }).data;
  if (!data || typeof data !== "object") return null;
  const message = (data as { error?: unknown }).error;
  return typeof message === "string" && message.trim() ? message.trim() : null;
}
